import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Eye, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { WorkViewer } from "@/components/WorkViewer";
import { PALETTE } from "@/components/MindMap";

export const WorkCard = ({ w, onOpen, onDelete, showAuthor }) => (
  <article data-testid={`work-card-${w.id}`} className="bg-white border border-zinc-200 hover:border-zinc-950 transition-colors duration-150 flex flex-col rise">
    <div className="flex h-1.5">{(w.mindmap?.branches || []).map((b, i) => <span key={`${b.title}-${i}`} className="flex-1" style={{ background: PALETTE[i % PALETTE.length].c }} />)}</div>
    <div className="p-5 flex-1">
      <div className="flex items-start gap-3 mb-3">
        <span className="text-3xl leading-none">{w.mindmap?.emoji}</span>
        <h3 className="font-display text-lg font-bold tracking-tight leading-snug">{w.title}</h3>
      </div>
      {showAuthor && <p className="text-sm font-semibold mb-1" data-testid="work-author">{w.author} · {w.classe}</p>}
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{fmtDate(w.created_at, true)} · {w.source_type}</p>
      <p className="text-sm text-zinc-600 mt-3 line-clamp-3">{w.extracted_text}</p>
    </div>
    <div className="flex border-t border-zinc-200">
      <button data-testid={`work-open-btn-${w.id}`} onClick={() => onOpen(w)} className="flex-1 flex items-center justify-center gap-2 h-11 text-sm font-semibold hover:bg-zinc-950 hover:text-white transition-colors duration-150">
        <Eye className="w-4 h-4" /> Apri & PDF
      </button>
      {onDelete && (
        <button data-testid={`work-delete-btn-${w.id}`} onClick={() => onDelete(w)} className="w-12 grid place-items-center border-l border-zinc-200 text-zinc-500 hover:bg-[#DC2626] hover:text-white transition-colors duration-150">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  </article>
);

export default function Works() {
  const [works, setWorks] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/works").then((r) => setWorks(r.data));
  useEffect(() => { load(); }, []);

  const del = async (w) => {
    if (!window.confirm(`Eliminare “${w.title}”?`)) return;
    try { await api.delete(`/works/${w.id}`); toast.success("Lavoro eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-widest text-[#0D9488] mb-3">Archivio personale</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-gradient tracking-tight mb-10">I miei Lavori</h1>
      {works && works.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-12 text-center" data-testid="works-empty">
          <FolderOpen className="w-10 h-10 mx-auto text-zinc-300 mb-4" strokeWidth={1.25} />
          <p className="font-display text-lg font-semibold mb-2">Nessun lavoro salvato</p>
          <Link to="/studio" className="text-sm underline underline-offset-4 hover:text-[#0D9488]" data-testid="works-empty-cta">Crea la tua prima mappa</Link>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="works-grid">
        {works?.map((w) => <WorkCard key={w.id} w={w} onOpen={setOpen} onDelete={del} />)}
      </div>
      <WorkViewer work={open} onClose={() => setOpen(null)} />
    </div>
  );
}
